import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Truck, RefreshCw, Award } from 'lucide-react';
import { Layout } from '../components/Layout';
import { ProductCard } from '../components/ProductCard';
import { getProducts, seedProducts } from '../services/api';
import { Button } from '../components/ui/button';

const heroImage = 'https://images.unsplash.com/photo-1648566924598-aeb39df9ad79?w=1920&q=80';

const benefits = [
  {
    icon: Truck,
    title: 'Free Worldwide Shipping',
    description: 'On orders above €150',
  },
  {
    icon: RefreshCw,
    title: 'Free Returns',
    description: '30-day hassle-free returns',
  },
  {
    icon: Award,
    title: 'Premium Quality',
    description: '100% authentic cashmere',
  },
];

const categories = [
  {
    name: 'Cashmere Capes',
    image: 'https://images.unsplash.com/photo-1612863234179-7e51d74c28a4?w=600&q=80',
    slug: 'cashmere-capes-ponchos',
  },
  {
    name: 'Pullovers',
    image: 'https://images.pexels.com/photos/7232494/pexels-photo-7232494.jpeg?w=600&q=80',
    slug: 'cashmere-pullovers',
  },
  {
    name: 'Tribal Collection',
    image: 'https://images.unsplash.com/photo-1609432585802-43712ac73214?w=600&q=80',
    slug: 'tribal-ponchos',
  },
];

export const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        // Try to seed products first
        await seedProducts();
      } catch (e) {
        // Products already seeded
      }
      
      try {
        const products = await getProducts(null, true);
        setFeaturedProducts(products.slice(0, 4));
      } catch (error) {
        console.error('Failed to load products:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[90vh] min-h-[600px] overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0 bg-[#2C2420]/40" />
        </div>
        
        <div className="relative h-full container-luxury flex items-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="max-w-2xl"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#E6DCC8] mb-4">
              Himalayan Heritage
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#F9F8F6] leading-tight mb-6">
              Timeless Elegance,{' '}
              <span className="italic">Woven by Hand</span>
            </h1>
            <p className="font-body text-base md:text-lg text-[#E6DCC8] mb-8 max-w-lg leading-relaxed">
              Discover our collection of premium cashmere and wool garments, 
              crafted by artisans using centuries-old Himalayan techniques.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/shop">
                <Button
                  data-testid="hero-shop-btn"
                  className="btn-primary bg-[#F9F8F6] text-[#2C2420] hover:bg-[#E6DCC8]"
                >
                  Shop Collection
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/about">
                <Button
                  variant="outline"
                  data-testid="hero-story-btn"
                  className="btn-secondary border-[#F9F8F6] text-[#F9F8F6] hover:bg-[#F9F8F6] hover:text-[#2C2420]"
                >
                  Our Story
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-px h-16 bg-gradient-to-b from-transparent to-[#F9F8F6]/50" />
        </motion.div>
      </section>

      {/* Benefits Bar */}
      <section className="bg-[#2C2420] py-8" data-testid="benefits-section">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex flex-col items-center gap-2"
              >
                <benefit.icon className="h-5 w-5 text-[#C6A87C]" />
                <p className="text-xs uppercase tracking-widest text-[#F9F8F6]">
                  {benefit.title}
                </p>
                <p className="text-sm text-[#8C8580]">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="section-spacing" data-testid="featured-products">
        <div className="container-luxury">
          <div className="text-center mb-16">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-xs uppercase tracking-[0.2em] text-[#C6A87C] mb-3"
            >
              Curated Selection
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-3xl md:text-4xl text-[#2C2420]"
            >
              Featured Collection
            </motion.h2>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : (
            <div className="product-grid">
              {featuredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/shop">
              <Button
                variant="outline"
                data-testid="view-all-products-btn"
                className="btn-secondary"
              >
                View All Products
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Category Grid */}
      <section className="section-spacing bg-[#F2EFE9]" data-testid="categories-section">
        <div className="container-luxury">
          <div className="text-center mb-16">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-xs uppercase tracking-[0.2em] text-[#C6A87C] mb-3"
            >
              Explore
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-3xl md:text-4xl text-[#2C2420]"
            >
              Shop by Category
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <Link
                  to={`/shop?category=${category.slug}`}
                  className="group block relative aspect-[4/5] overflow-hidden"
                  data-testid={`category-${category.slug}`}
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-[#2C2420]/30 transition-opacity duration-300 group-hover:opacity-50" />
                  <div className="absolute inset-0 flex items-end p-8">
                    <div>
                      <h3 className="font-heading text-2xl text-[#F9F8F6] mb-2">
                        {category.name}
                      </h3>
                      <span className="text-xs uppercase tracking-widest text-[#E6DCC8] flex items-center gap-2 group-hover:gap-4 transition-all duration-300">
                        Explore
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Story Teaser */}
      <section className="section-spacing" data-testid="brand-story-section">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1767390552768-6703f91c2518?w=800&q=80"
                alt="Artisan weaving"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-[#C6A87C] hidden lg:block" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:pl-8"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-[#C6A87C] mb-3">
                Our Heritage
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#2C2420] mb-6">
                The Art of Himalayan Craftsmanship
              </h2>
              <p className="font-accent text-xl italic text-[#5D5550] mb-6 leading-relaxed">
                "Every thread tells a story of tradition, passed down through generations 
                of master artisans."
              </p>
              <p className="text-base text-[#5D5550] mb-8 leading-relaxed">
                At Shiva Shakti, we honor the sacred balance between ancient wisdom and 
                modern elegance. Our garments are crafted using techniques that have been 
                perfected over centuries in the high Himalayan villages.
              </p>
              <Link to="/about">
                <Button
                  variant="outline"
                  data-testid="read-story-btn"
                  className="btn-secondary"
                >
                  Read Our Story
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
};
